/**
 * Copyright 2020 New Light Technologies, Inc.
 *
 * With Supporting Sponsorship from the Federal Emergency Management Agency (Contract: GSA Stars II GS-06F-0968Z)
 * In accordance with FAR 52.227-14(c)(iii), New Light Technologies, Inc. grants to the Government and others acting on its behalf a paid-up, nonexclusive, irrevocable, worldwide license in such copyrighted computer software and data to reproduce, prepare derivative works, and perform publicly and display publicly (but not to distribute copies to the public) by or on behalf of the Government.
 * Any other use, distribution, reproduction, modification, or publication without the prior express written authorization of New Light Technologies, Inc. is strictly prohibited.
 * All other rights are reserved by their respective copyright holders.
 *
 */
import React from 'react';
import { FemaRegions } from '../Regions/FemaRegions';
import { MapStylePicker } from '../map/MapStylePicker';
import { PostLegend } from '../Post/PostLayer';
import {
  Menu,
  MenuItem,
  Select,
  SelectChangeEvent,
  ToggleButton,
  Tooltip,
  Typography,
  ListItemIcon,
  ListItemText,
  Box,
  Button,
  Divider,
} from '@mui/material';
import { useCallback } from 'react';
import { useMatch, useSearchParams } from 'react-router-dom';
import { IncidentLegend } from '../Incident/IncidentLegend';
import { IncidentSection } from '../pages/IncidentPage';
import { PrimaryLayer } from '../pages/MapPage';
import { Suspense } from 'react';
import LayersIcon from '@mui/icons-material/Layers';
import CenterFocusStrong from '@mui/icons-material/CenterFocusStrong';
import { WowLegend } from '../wow/WowLayer';
import { CountyLegend } from '../Incident/CountyLayer';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { InfoModal } from '../components/InfoModal';
import { layerVisibility, viewingHistoricalAtom, viewStateAtom } from './atoms';
import { Business, Check, VisibilityOff } from '@mui/icons-material';
import {
  viewStructuresAtom,
  StructureViewTypes,
  StructuresLegend,
  StructuresStatus,
  STRUCTURES_MIN_ZOOM,
} from './layers/StructuresLayer';
import { userAtom } from '../auth/userAtoms';
import type { AuthenticatedUser } from '../auth/userAtoms';
import { TingOutagesStatus, TingLegend } from '../ting/TingOutagesLayer';
import {
  EXPOSURE_CHECK_FAILED_HINT,
  type ExposureAvailability,
  useCheckStructuresData,
  useExposureEventId,
} from './layers/StructuresLayer';
import { enableCanopy } from '../constants';
import { extentStateAtom } from '../Incident/atoms';
import { underlaySectionsFor } from '../Incident/incidentHooks';
import { IncidentDateSelect, IncidentRunAvailability } from './IncidentDateSelect';
import { LayerControls } from './layerControls/LayerControls';
import { BottomLeftPanel } from './layerControls/BottomLeftPanel';
import { ColorByControl } from './layerControls/ColorByControl';
import { SwipeControl } from './layerControls/SwipeControl';
import { ThreeDViewControls } from './layerControls/ThreeDViewControls';
import { PanelField, PanelSection, panelSelectSx } from './layerControls/panelFields';
import { useLayerState } from './layerControls/useLayerState';
import { ACCENT, WHITE } from './layerControls/palette';

type MapControlProps = {
  selectedMap?: PrimaryLayer;
  setSelectedMap?: React.Dispatch<React.SetStateAction<PrimaryLayer>>;
  context?: 'wowcast' | 'default';
  children?: React.ReactNode;
};

/** The products the dashboard's POST grid legend and attribute controls cover. */
const POST_PRODUCTS: readonly string[] = ['wow', 'nifc', 'canopy'];

const PRODUCT_HINT =
  'The 24/7 monitoring product drawn on the map: its events and, where the product runs one, ' +
  'the POST grid ranking the exposed population.';
const ZOOM_TO_EXTENT_HINT = 'Fit the map to the area this section of the incident covers.';
const SECTION_DIVIDER_SX = { my: 1, borderColor: 'rgba(255,255,255,0.25)' } as const;

export const MapControls = React.memo((props: MapControlProps) => {
  const [user] = useAtom(userAtom);

  const { selectedMap, setSelectedMap } = props;
  const [viewStructures, setViewStructures] = useAtom(viewStructuresAtom);
  const hightlightCounties = useAtomValue(layerVisibility('county-highlights'));
  const viewingHistorical = useAtomValue(viewingHistoricalAtom);
  const layerState = useLayerState();
  const setExtentState = useSetAtom(extentStateAtom);
  const [searchParams] = useSearchParams();
  // The exact gate useHomeLayers (pages/Home.tsx) applies to the live layers:
  // a hand-typed ?timeseries hides them just as the time slider does.
  const timeseriesEnabled = searchParams.has('timeseries') || viewingHistorical;

  const isHome = useMatch({ path: '/' });
  const isWowcast = useMatch({ path: '/wowcast' });
  const mapPage = useMatch({
    path: '/:incidentId/:sectionId/:viewType/:attributeId?',
  });
  const isIncidentcast = useMatch({ path: '/wowcast/:incidentId/:sectionId' });
  const isIncidentPage = isIncidentcast || mapPage;

  const sectionId = (isIncidentPage?.params?.sectionId ?? 'population') as IncidentSection;
  const incidentId = isIncidentPage?.params?.incidentId;
  const exposureEventId = useExposureEventId();
  const exposureAvailability = useCheckStructuresData(exposureEventId);

  // The section whose grid is on the map, which decides the attribute
  // controls offered: the incident section on an incident view, the selected
  // product's POST grid on the dashboard, nothing otherwise.
  const attributeSectionId =
    isIncidentPage != null
      ? sectionId
      : isHome != null && selectedMap != null && POST_PRODUCTS.includes(selectedMap)
        ? `${selectedMap}-post`
        : null;

  const handleChange = useCallback(
    (event: SelectChangeEvent) => {
      if (setSelectedMap) {
        setSelectedMap(event.target.value as PrimaryLayer);
      }
    },
    [setSelectedMap]
  );

  const renderModelSelect = () =>
    isHome != null ? (
      <PanelField id="product-label" label="Product" hint={PRODUCT_HINT}>
        <Select
          fullWidth
          labelId="product-label"
          id="layer-select"
          value={selectedMap}
          variant="outlined"
          size="small"
          sx={{
            ...panelSelectSx,
            fontWeight: 600,
            color: theme => theme.palette.primary.main,
          }}
          onChange={handleChange}
        >
          <MenuItem dense value="wow">
            WOW (Worst of the Worst)
          </MenuItem>
          <MenuItem dense value="nifc">
            NIFC
          </MenuItem>
          {enableCanopy && (
            <MenuItem dense value="canopy">
              Canopy
            </MenuItem>
          )}
          <MenuItem dense value="ting">
            Ting Power Outages
          </MenuItem>
        </Select>
      </PanelField>
    ) : null;

  // The compact active-layer legend swatches (always visible in the redesign).
  const renderLegend = () => (
    <>
      {(isHome != null || isWowcast != null) && (
        <>
          <Box sx={{ width: 'fit-content', overflow: 'auto' }}>
            {selectedMap != null && POST_PRODUCTS.includes(selectedMap) && (
              <Box sx={{ mb: 1 }}>
                <PostLegend selectedMap={selectedMap} />
              </Box>
            )}
            {selectedMap === 'wow' && <WowLegend />}
            {selectedMap === 'ting' && <TingLegend />}
          </Box>
          {viewStructures.view && viewStructures.filter !== 'highlight' && (
            <Box sx={{ mt: 1 }}>
              <StructuresLegend />
            </Box>
          )}
        </>
      )}
      {viewStructures.view && (
        <StructuresStatus
          exposureEventId={exposureEventId}
          exposureAvailability={exposureAvailability}
        />
      )}
      {/* The status line reports the LIVE outage count, so it may only show
          while those polygons are the ones on the map: the Ting layer draws
          on the home route alone, a time-series view replaces it with
          history polygons, and the layer list's eye can hide it outright. */}
      {isHome != null &&
        selectedMap === 'ting' &&
        !timeseriesEnabled &&
        layerState.isLeafShown('ting-outages', 'event') && <TingOutagesStatus />}

      <Suspense fallback={null}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            position: 'relative',
            top: '0',
          }}
        >
          {isIncidentPage != null && (
            <Box
              sx={{
                pointerEvents: 'auto',
                maxHeight: 'calc(100svh - 298px)',
                minHeight: '50px',
                overflow: 'auto',
                overflowX: 'hidden',
                scrollPadding: '8px',
                borderRadius: '4px',
                mb: 0,
              }}
            >
              {hightlightCounties && (
                <Box sx={{ mb: 1, pointerEvents: 'auto' }}>
                  <CountyLegend />
                </Box>
              )}
              <Box sx={{ display: 'flex', flexDirection: 'row' }}>
                <Box sx={{ pointerEvents: 'auto' }}>
                  <IncidentLegend sectionId={sectionId} />
                </Box>
              </Box>
              {/* Always mounted with the legend, so the newest model run of
                  every section this page draws is selected whether or not the
                  controls panel is open. */}
              {incidentId != null &&
                [sectionId, ...underlaySectionsFor(sectionId)].map(section => (
                  <IncidentRunAvailability
                    key={section}
                    incidentId={incidentId}
                    sectionId={section}
                  />
                ))}
            </Box>
          )}
        </Box>
      </Suspense>
    </>
  );

  // Every control that acts on the map, in three groups: the data on it, the
  // ways of viewing that data (each view tool reveals its own settings only
  // while it is on), and the layers drawn. The swipe tool only mounts on the
  // default map (Map.tsx), so its toggle is hidden on the wowcast mini map.
  const renderControls = (showSwipe: boolean) => (
    <Box sx={{ pointerEvents: 'auto' }}>
      {(isHome != null || isIncidentPage != null) && (
        <>
          <PanelSection title="Data">
            {renderModelSelect()}
            {isIncidentPage != null && incidentId != null && (
              <Suspense fallback={null}>
                <IncidentDateSelect incidentId={incidentId} sectionId={sectionId} />
              </Suspense>
            )}
            {attributeSectionId != null && <ColorByControl sectionId={attributeSectionId} />}
          </PanelSection>
          <Divider sx={SECTION_DIVIDER_SX} />
        </>
      )}

      <PanelSection title="View">
        {isIncidentPage != null && (
          <Tooltip title={ZOOM_TO_EXTENT_HINT} placement="right" describeChild>
            <Button
              variant="outlined"
              size="small"
              startIcon={<CenterFocusStrong />}
              onClick={() => setExtentState('zoom-to-extent')}
              sx={{
                width: '100%',
                mb: 1,
                color: WHITE,
                borderColor: WHITE,
                fontWeight: 700,
                '&:hover': { borderColor: WHITE, backgroundColor: 'rgba(255,255,255,0.12)' },
                '&:focus-visible': { outline: `2px solid ${ACCENT}` },
              }}
            >
              Zoom to incident extent
            </Button>
          </Tooltip>
        )}
        {showSwipe && (
          <Box sx={{ mb: 1 }}>
            <SwipeControl />
          </Box>
        )}
        <ThreeDViewControls sectionId={attributeSectionId} />
      </PanelSection>
      <Divider sx={SECTION_DIVIDER_SX} />

      <PanelSection title="Layers">
        <LayerControls />
      </PanelSection>
    </Box>
  );

  const renderFooter = () => {
    return (
      <Box
        data-print="hidden"
        sx={{
          alignItems: 'top',
          display: 'flex',
          flexDirection: 'row',
          flexWrap: 'wrap',
          pointerEvents: 'auto',
          justifyContent: 'space-between',
          pt: 1,
          ml: 1,
          mb: 1,
          '> *': {
            mr: 1,
          },
          '@media print': {
            display: 'none!important',
          },
        }}
      >
        <MapStylePicker />
        <FemaRegions />
        <StructuresControls
          user={user}
          viewStructures={viewStructures}
          setViewStructures={setViewStructures}
          exposureAvailability={exposureAvailability}
          exposureUnavailableHint={
            isIncidentPage ? INCIDENT_EXPOSURE_UNAVAILABLE_HINT : EXPOSURE_UNAVAILABLE_HINT
          }
        />
      </Box>
    );
  };

  // The default map uses the bottom-left container (always-on compact legend
  // + a "Map Controls" reveal); wowcast keeps its fixed panel.
  if (props.context !== 'wowcast') {
    return (
      <>
        <BottomLeftPanel
          legend={renderLegend()}
          controls={renderControls(true)}
          footer={renderFooter()}
        />
        <InfoModal type={isIncidentPage != null ? sectionId : selectedMap} />
      </>
    );
  }

  return (
    <Box
      sx={{
        backgroundColor: 'rgba(0, 62, 103, 0.6)',
        backdropFilter: 'blur(4px)',
        borderRadius: '4px',
        left: 16,
        '@media (max-width: 1200px)': {
          transform: 'scale(0.9)',
          transformOrigin: 'bottom left',
        },
        bottom: 8,
        position: 'absolute',
        maxWidth: 260,
        p: 1,
        zIndex: 1000,
      }}
    >
      <Box
        sx={{
          overflow: 'scroll',
          maxHeight: 'calc(100vh - 300px)',
        }}
      >
        <Typography
          variant="h6"
          sx={{
            color: 'white',
            textIndent: '10px',
            alignItems: 'center',
            display: 'flex',
            pb: 1,
          }}
        >
          <LayersIcon /> Map Controls
        </Typography>

        {renderLegend()}

        <Box sx={{ mt: 1 }}>{renderControls(false)}</Box>
      </Box>
    </Box>
  );
});

type StructuresControlsProps = {
  user: AuthenticatedUser | null;
  viewStructures: { view: boolean; filter: StructureViewTypes };
  setViewStructures: (value: { view: boolean; filter: StructureViewTypes }) => void;
  exposureAvailability: ExposureAvailability;
  exposureUnavailableHint: string;
};

// The Home map keys exposure on the clicked WoW threat or Canopy swath; an
// incident view keys it on the incident itself and draws neither layer, so
// there is nothing to click.
const EXPOSURE_UNAVAILABLE_HINT =
  'Click a WoW threat or Canopy swath that has structure exposure analysis data to enable';
const INCIDENT_EXPOSURE_UNAVAILABLE_HINT =
  'Available when the incident has structure exposure analysis data';
// Shown between an event click and its availability answer, when the
// "click an event" hint would ask for what the user just did.
const EXPOSURE_CHECKING_HINT = 'Checking for structure exposure analysis data...';
const STRUCTURES_HINT =
  'Draw the USA Structures building footprints. They appear once the map is zoomed in far ' +
  'enough to show individual buildings.';
const STRUCTURES_HIDDEN_HINT = 'Structures are hidden -- zoom in to display them';

// Shown in the button label so the active structures view is visible at a
// glance without opening the menu
const STRUCTURE_FILTER_LABELS: Record<StructureViewTypes, string> = {
  occupancy_type: 'Occupancy',
  exposure: 'Exposed',
  highlight: 'Highlight',
  none: '',
};

const STRUCTURE_MENU_ITEMS: {
  filter: StructureViewTypes;
  label: string;
  needsExposureData: boolean;
}[] = [
  { filter: 'none', label: 'Clear', needsExposureData: false },
  { filter: 'occupancy_type', label: 'Structures by Occupancy Type', needsExposureData: false },
  { filter: 'exposure', label: 'Exposed Structures', needsExposureData: true },
  { filter: 'highlight', label: 'Highlight Exposed Structures', needsExposureData: true },
];

const StructuresControls: React.FC<StructuresControlsProps> = ({
  user,
  viewStructures,
  setViewStructures,
  exposureAvailability,
  exposureUnavailableHint,
}) => {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [, setLayerVisibliity] = useAtom(layerVisibility('structures'));
  const viewState = useAtomValue(viewStateAtom);
  // A structures view is on but the map is too far out to display it
  const hiddenByZoom = viewStructures.view && (viewState.zoom ?? 0) < STRUCTURES_MIN_ZOOM;

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClick = (filter: StructureViewTypes) => {
    if (filter === 'none') {
      setViewStructures({ view: false, filter: 'occupancy_type' });
      setLayerVisibliity(false);
    } else {
      setViewStructures({ view: true, filter });
      setLayerVisibliity(true);
    }
    handleClose();
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <Box
      sx={{
        overflow: 'hidden',
        borderRadius: '4px',
        height: 'fit-content',
        width: 'fit-content',
        mb: 1,
      }}
    >
      {!user && (
        <Tooltip title={hiddenByZoom ? STRUCTURES_HIDDEN_HINT : STRUCTURES_HINT} describeChild>
          <ToggleButton
            size="small"
            value="structures"
            selected={viewStructures.view}
            onChange={() =>
              setViewStructures({
                filter: 'occupancy_type',
                view: !viewStructures.view,
              })
            }
            sx={{
              border: `2px ${hiddenByZoom ? 'dashed' : 'solid'}`,
              backgroundColor: 'rgb(0, 82, 136, 1)',
            }}
          >
            <Business sx={{ pr: 0.75 }} />
            USA Structures
            {hiddenByZoom && <VisibilityOff fontSize="small" sx={{ pl: 0.75 }} />}
          </ToggleButton>
        </Tooltip>
      )}
      {user && (
        <>
          <Tooltip title={hiddenByZoom ? STRUCTURES_HIDDEN_HINT : STRUCTURES_HINT} describeChild>
            <Button
              aria-controls="simple-menu"
              aria-haspopup="true"
              onClick={handleClick}
              variant="outlined"
              size="small"
              sx={{
                color: 'white',
                backgroundColor: 'rgb(0, 82, 136, 1)',
                // Solid border: a view is active and drawing. Dashed border +
                // the VisibilityOff icon: a view is active but the map is
                // below the structures zoom floor, so nothing is drawn.
                border: viewStructures.view
                  ? `2px ${hiddenByZoom ? 'dashed' : 'solid'} white`
                  : undefined,
              }}
            >
              <Business sx={{ pr: 0.75 }} />
              {viewStructures.view
                ? `USA Structures: ${STRUCTURE_FILTER_LABELS[viewStructures.filter]}`
                : 'USA Structures'}
              {hiddenByZoom && <VisibilityOff fontSize="small" sx={{ pl: 0.75 }} />}
            </Button>
          </Tooltip>
          <Menu
            id="simple-menu"
            anchorEl={anchorEl}
            keepMounted
            open={Boolean(anchorEl)}
            onClose={handleClose}
          >
            {STRUCTURE_MENU_ITEMS.map(({ filter, label, needsExposureData }) => {
              // A checkmark marks the active item: selection must not rely on
              // the background tint alone, which color-deficient users (and
              // anyone in glare) can miss.
              const selected =
                filter === 'none'
                  ? !viewStructures.view
                  : viewStructures.view && viewStructures.filter === filter;
              const disabled = needsExposureData && exposureAvailability !== 'available';
              const item = (
                <MenuItem
                  key={filter}
                  onClick={() => handleMenuClick(filter)}
                  disabled={disabled}
                  selected={selected}
                >
                  <ListItemIcon>{selected && <Check fontSize="small" />}</ListItemIcon>
                  <ListItemText>{label}</ListItemText>
                </MenuItem>
              );
              return disabled ? (
                <Tooltip
                  key={filter}
                  title={
                    exposureAvailability === 'error'
                      ? EXPOSURE_CHECK_FAILED_HINT
                      : exposureAvailability === 'checking'
                        ? EXPOSURE_CHECKING_HINT
                        : exposureUnavailableHint
                  }
                  placement="right"
                >
                  <span>{item}</span>
                </Tooltip>
              ) : (
                item
              );
            })}
          </Menu>
        </>
      )}
    </Box>
  );
};
